import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getTranscribeQuota,
  getTranscribeQuotaDay,
  getTranscribeQuotaJob,
  resumeTranscribeQuota,
  denyTranscribeQuota,
  setDefaultQuotaLimit
} from '../api/api';

const BADGE = {
  pca:     { label: 'PCA',     bg: 'bg-blue-100 text-blue-800' },
  bulk:    { label: 'Bulk',    bg: 'bg-purple-100 text-purple-800' },
  genesys: { label: 'Genesys', bg: 'bg-orange-100 text-orange-800' }
};

function WorkflowBadges({ types = [] }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {[...types].sort().map(t => {
        const b = BADGE[t] || { label: t, bg: 'bg-gray-100 text-gray-800' };
        return (
          <span key={t} className={`px-2 py-0.5 rounded text-xs font-medium ${b.bg}`}>
            {b.label}
          </span>
        );
      })}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

// Show exact count for PCA (1 per call), approximate for Genesys (chunk × 100 estimate)
function CallCount({ job }) {
  const count = job.callCount ?? job.stoppedCount ?? 0;
  const isGenesys = (job.workflowTypes || []).includes('genesys') &&
                    !(job.workflowTypes || []).includes('pca');
  return (
    <span className="font-semibold text-gray-700">
      {isGenesys ? `~${count}` : count}
    </span>
  );
}

export default function TranscribeQuota({ setAlert }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [expandedJob, setExpandedJob] = useState(null);
  const [expandedExecs, setExpandedExecs] = useState([]);
  const [expandLoading, setExpandLoading] = useState(false);
  const [newLimit, setNewLimit] = useState('');
  const [defaultLimit, setDefaultLimit] = useState('');
  const [defaultLimitLoading, setDefaultLimitLoading] = useState(false);
  const [defaultLimitMsg, setDefaultLimitMsg] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  // Per-day pagination: { "2026-05-24": "<base64nextKey>", ... }
  const [dayNextKeys, setDayNextKeys] = useState({});
  const [dayLoadingMore, setDayLoadingMore] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTranscribeQuota();
      setData(res);
      if (newLimit === '') setNewLimit(String(res.days?.[0]?.limit || 500));
      // Seed nextKey map from initial response
      const keys = {};
      (res.days || []).forEach(d => { if (d.nextKey) keys[d.date] = d.nextKey; });
      setDayNextKeys(keys);
    } catch (e) {
      setAlert && setAlert({ type: 'error', text: 'Failed to load call limiter data' });
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Load the next page of jobs for a specific date
  const loadMoreForDay = async (date) => {
    const startKey = dayNextKeys[date];
    if (!startKey) return;
    setDayLoadingMore(prev => ({ ...prev, [date]: true }));
    try {
      const res = await getTranscribeQuotaDay(date, startKey);
      setData(prev => ({
        ...prev,
        days: prev.days.map(d =>
          d.date === date
            ? { ...d, jobs: [...d.jobs, ...(res.jobs || [])], stoppedJobCount: d.stoppedJobCount + (res.jobs || []).length }
            : d
        ),
      }));
      setDayNextKeys(prev => {
        const next = { ...prev };
        if (res.nextKey) next[date] = res.nextKey;
        else delete next[date];
        return next;
      });
    } catch {
      setAlert && setAlert({ type: 'error', text: `Failed to load more paused jobs for ${date}` });
    } finally {
      setDayLoadingMore(prev => ({ ...prev, [date]: false }));
    }
  };

  // Composite key so same jobId on different days is distinct
  const jobKey = (date, jobId) => `${date}|${jobId}`;
  const parseJobKey = (key) => ({ date: key.slice(0, 10), jobId: key.slice(11) });

  // allJobs keeps date attached for capacity lookups; allJobKeys drives checkbox state
  const allJobs = data?.days?.flatMap(d => d.jobs.map(j => ({ ...j, date: d.date }))) || [];
  const allJobKeys = data?.days?.flatMap(d => d.jobs.map(j => jobKey(d.date, j.jobId))) || [];
  const today = data?.days?.[0] || {};

  const toggleSelect = (date, jobId) => {
    const key = jobKey(date, jobId);
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === allJobKeys.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allJobKeys));
    }
  };

  const expandJob = async (date, jobId) => {
    const key = jobKey(date, jobId);
    if (expandedJob === key) { setExpandedJob(null); return; }
    setExpandedJob(key);
    setExpandLoading(true);
    try {
      const res = await getTranscribeQuotaJob(jobId, date);
      setExpandedExecs(res.executions || []);
    } catch {
      setExpandedExecs([]);
    } finally {
      setExpandLoading(false);
    }
  };

  // Execute the staged action after "confirm" is typed
  const doExecute = async (action) => {
    setPendingAction(null);
    setConfirmText('');
    setActionLoading(true);
    setActionMsg(null);
    try {
      if (action.type === 'deny') {
        const res = await denyTranscribeQuota({ jobs: action.jobs });
        setActionMsg({ type: 'success', text: `Denied ${res.deniedJobs} job(s).` });
      } else {
        // 'resume', 'set-zero', or 'set-limit'
        const limitOverride = action.newLimit !== undefined
          ? action.newLimit
          : (newLimit ? parseInt(newLimit, 10) : undefined);
        const payload = { jobs: action.jobs };
        if (limitOverride !== undefined) payload.newLimit = limitOverride;
        const res = await resumeTranscribeQuota(payload);
        if (action.type === 'set-zero') {
          setActionMsg({ type: 'success', text: `Today's call limit set to 0. All new calls will be paused.` });
          setNewLimit('0');
        } else if (action.type === 'set-limit') {
          setActionMsg({ type: 'success', text: `Today's call limit updated to ${limitOverride}.` });
        } else {
          setActionMsg({ type: 'success', text: `Resumed ${res.resumedJobs} job(s), ${res.resumedExecutions} execution(s) restarted.` });
        }
      }
      setSelected(new Set());
      await load();
    } catch (e) {
      setActionMsg({ type: 'error', text: `${action.type === 'deny' ? 'Deny' : 'Resume'} failed: ${e.message}` });
    } finally {
      setActionLoading(false);
    }
  };

  const doSetDefault = async () => {
    const val = parseInt(defaultLimit, 10);
    if (!val || val < 1) return;
    setDefaultLimitLoading(true);
    setDefaultLimitMsg(null);
    try {
      await setDefaultQuotaLimit(val);
      setDefaultLimitMsg({ type: 'success', text: `Default call limit updated to ${val}. Takes effect from tomorrow's reset.` });
      setDefaultLimit('');
    } catch (e) {
      setDefaultLimitMsg({ type: 'error', text: 'Failed to update default call limit: ' + e.message });
    } finally {
      setDefaultLimitLoading(false);
    }
  };

  const usedPct = today.limit ? Math.min(100, Math.round((today.used / today.limit) * 100)) : 0;
  const barColor = usedPct >= 90 ? 'bg-red-500' : usedPct >= 70 ? 'bg-yellow-400' : 'bg-green-500';
  const selJobs = [...selected].map(parseJobKey);  // [{jobId, date}]
  const totalStopped = allJobKeys.length;

  const jobCallCount = (job) => job.callCount ?? job.stoppedCount ?? 0;
  const overrideAboveDailyLimit = !!(newLimit && parseInt(newLimit, 10) > (today.limit || 0));
  const canResumeJob = () => overrideAboveDailyLimit;
  const resumeBlockedReason = () =>
    `Override call limit must be greater than the daily call limit (${today.limit ?? 0}). Enter a higher override value above.`;

  const selectedCallsTotal = selJobs.reduce((sum, { jobId, date }) => {
    const job = allJobs.find(j => j.jobId === jobId && j.date === date);
    return sum + (job ? jobCallCount(job) : 0);
  }, 0);
  const canResumeSelected = overrideAboveDailyLimit;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Call Limiter</h1>
        <p className="text-sm text-gray-500 mt-1">Daily call transcription limit — manage paused executions</p>
      </div>

      {/* Today's stats row */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Daily Call Limit', value: today.limit },
            { label: 'Used Today', value: today.used },
            { label: 'Remaining Today', value: today.remaining }
          ].map(({ label, value }) => (
            <div key={label} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <div className="text-sm text-gray-500">{label}</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">{value ?? '—'}</div>
            </div>
          ))}
        </div>
      )}

      {data && (
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Today's usage</span>
            <span>{usedPct}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2.5">
            <div className={`${barColor} h-2.5 rounded-full transition-all`} style={{ width: `${usedPct}%` }} />
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-4">
        {/* Resume / deny row */}
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Override today's call limit (optional)</label>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                min="0"
                className="border border-gray-300 rounded px-3 py-1.5 text-sm w-36"
                value={newLimit}
                onChange={e => setNewLimit(e.target.value)}
                placeholder={String(today?.limit || 500)}
              />
              <button
                disabled={!newLimit || actionLoading}
                onClick={() => {
                  const val = parseInt(newLimit, 10);
                  if (isNaN(val)) return;
                  setPendingAction({
                    type: 'set-limit',
                    jobs: [],
                    newLimit: val,
                    label: `Set today's call limit to ${val}`
                  });
                }}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-40"
              >
                Apply
              </button>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              disabled={selJobs.length === 0 || actionLoading || !canResumeSelected}
              onClick={() => setPendingAction({
                type: 'resume',
                jobs: selJobs,
                label: `Resume ${selJobs.length} job(s)`
              })}
              title={!canResumeSelected && selJobs.length > 0 ? `Override call limit must be greater than the daily call limit (${today.limit ?? 0}). Enter a higher override value above.` : undefined}
              className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-40"
            >
              Resume Selected ({selJobs.length})
            </button>
            <button
              disabled={selJobs.length === 0 || actionLoading}
              onClick={() => setPendingAction({
                type: 'deny',
                jobs: selJobs,
                label: `Deny ${selJobs.length} job(s)`
              })}
              className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-40"
            >
              Deny Selected ({selJobs.length})
            </button>
            <button
              onClick={() => setPendingAction({
                type: 'set-zero',
                jobs: [],
                newLimit: 0,
                label: "Set today's call limit to 0"
              })}
              disabled={actionLoading}
              className="px-4 py-2 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-40"
            >
              Set Limit to 0
            </button>
            <button
              onClick={load}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded border hover:bg-gray-200"
            >
              Refresh
            </button>
          </div>
          {actionMsg && (
            <span className={`text-sm ${actionMsg.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
              {actionMsg.text}
            </span>
          )}
        </div>

        {/* Confirmation panel */}
        {pendingAction && (
          <div className="flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3">
            <span className="text-amber-700 font-medium text-sm">
              ⚠ {pendingAction.label} — type <strong>confirm</strong> to proceed
            </span>
            <input
              type="text"
              autoFocus
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && confirmText.toLowerCase() === 'confirm') doExecute(pendingAction);
                if (e.key === 'Escape') { setPendingAction(null); setConfirmText(''); }
              }}
              placeholder="type confirm"
              className="border border-amber-400 rounded px-3 py-1.5 text-sm w-36 bg-white"
            />
            <button
              disabled={confirmText.toLowerCase() !== 'confirm'}
              onClick={() => doExecute(pendingAction)}
              className="px-4 py-1.5 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-40"
            >
              Confirm
            </button>
            <button
              onClick={() => { setPendingAction(null); setConfirmText(''); }}
              className="px-4 py-1.5 bg-gray-100 text-gray-700 text-sm rounded border hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Default limit row */}
        <div className="flex flex-wrap items-end gap-4 border-t border-gray-100 pt-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">New daily call limit (SSM default)</label>
            <p className="text-xs text-gray-400 mb-1">Takes effect from tomorrow's midnight reset onwards</p>
            <input
              type="number"
              min="1"
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-36"
              value={defaultLimit}
              onChange={e => setDefaultLimit(e.target.value)}
              placeholder="e.g. 500"
            />
          </div>
          <button
            disabled={!defaultLimit || defaultLimitLoading}
            onClick={doSetDefault}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-40"
          >
            {defaultLimitLoading ? 'Saving…' : 'Set Default Call Limit'}
          </button>
          {defaultLimitMsg && (
            <span className={`text-sm ${defaultLimitMsg.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
              {defaultLimitMsg.text}
            </span>
          )}
        </div>
      </div>

      {/* Jobs table — grouped by day */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700">
            Paused Jobs {data ? `(${totalStopped})` : ''}
          </h2>
          <span className="text-xs text-gray-400">Last 7 days</span>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading...</div>
        ) : !data || allJobKeys.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No paused jobs in the last 7 days.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allJobKeys.length > 0 && selected.size === allJobKeys.length}
                    onChange={toggleAll}
                  />
                </th>
                <th className="px-4 py-3 text-left">Job ID</th>
                <th className="px-4 py-3 text-left">Campaign</th>
                <th className="px-4 py-3 text-left">Scorecard</th>
                <th className="px-4 py-3 text-left">Uploaded By</th>
                <th className="px-4 py-3 text-left">Workflows</th>
                <th className="px-4 py-3 text-right">Last Paused</th>
                <th className="px-4 py-3 text-right">Calls</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.days.map(day => (
                <React.Fragment key={day.date}>
                  {/* Day separator row */}
                  <tr className="bg-gray-50">
                    <td colSpan={9} className="px-4 py-2">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-gray-600">{day.date}</span>
                        <span className="text-xs text-gray-400">
                          {day.stoppedJobCount} paused job{day.stoppedJobCount !== 1 ? 's' : ''}
                          {' · '}limit {day.limit} · used {day.used}
                        </span>
                      </div>
                    </td>
                  </tr>

                  {day.jobs.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-8 py-3 text-xs text-gray-400 italic">No paused jobs this day.</td>
                    </tr>
                  ) : (
                    <>
                      {day.jobs.map(job => (
                        <React.Fragment key={job.jobId}>
                          <tr
                            className={`hover:bg-gray-50 cursor-pointer ${selected.has(jobKey(day.date, job.jobId)) ? 'bg-blue-50' : ''}`}
                            onClick={() => toggleSelect(day.date, job.jobId)}
                          >
                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                              <input type="checkbox" checked={selected.has(jobKey(day.date, job.jobId))} onChange={() => toggleSelect(day.date, job.jobId)} />
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-gray-700 max-w-[180px]" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center gap-1 min-w-0">
                                <Link
                                  to={`/jobs/${job.jobId}`}
                                  className="text-blue-600 hover:underline truncate"
                                  title="View job detail"
                                >
                                  {job.jobId}
                                </Link>
                                <button
                                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 ml-1"
                                  onClick={() => expandJob(day.date, job.jobId)}
                                  title={expandedJob === jobKey(day.date, job.jobId) ? 'Hide calls' : 'View paused calls'}
                                >
                                  {expandedJob === jobKey(day.date, job.jobId) ? '▲' : '▼'}
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-700">{job.campaign || '—'}</td>
                            <td className="px-4 py-3 text-gray-700">{job.scorecard || '—'}</td>
                            <td className="px-4 py-3 text-gray-500">{job.uploadedBy || '—'}</td>
                            <td className="px-4 py-3">
                              <WorkflowBadges types={job.workflowTypes} />
                            </td>
                            <td className="px-4 py-3 text-right text-gray-500 whitespace-nowrap">
                              {fmt(job.lastStoppedAt)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <CallCount job={job} />
                            </td>
                            <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                              <div className="flex justify-end gap-2">
                                <div className="relative group">
                                  <button
                                    onClick={() => setPendingAction({
                                      type: 'resume',
                                      jobs: [{ jobId: job.jobId, date: day.date }],
                                      label: `Resume job ${job.jobId}`
                                    })}
                                    disabled={actionLoading || !canResumeJob(job)}
                                    className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    Resume
                                  </button>
                                  <div className="absolute bottom-full right-0 mb-2 hidden group-hover:flex items-start gap-1.5 bg-amber-50 border border-amber-400 text-amber-900 text-xs rounded-md px-3 py-2 shadow-lg w-64 z-50 pointer-events-none">
                                    <span className="mt-0.5 text-amber-500 font-bold text-sm leading-none">⚠</span>
                                    <span className="font-medium leading-snug">
                                      {canResumeJob(job)
                                        ? 'Make sure that you have increased the daily call limit before resuming'
                                        : resumeBlockedReason(job)}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => setPendingAction({
                                    type: 'deny',
                                    jobs: [{ jobId: job.jobId, date: day.date }],
                                    label: `Deny job ${job.jobId}`
                                  })}
                                  disabled={actionLoading}
                                  className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-40"
                                >
                                  Deny
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded calls row */}
                          {expandedJob === jobKey(day.date, job.jobId) && (
                            <tr>
                              <td colSpan={9} className="bg-gray-50 px-8 py-4">
                                {expandLoading ? (
                                  <p className="text-sm text-gray-400">Loading paused calls…</p>
                                ) : expandedExecs.length === 0 ? (
                                  <p className="text-sm text-gray-400">No paused call records found.</p>
                                ) : (
                                  <table className="w-full text-xs">
                                    <thead className="text-gray-400 uppercase">
                                      <tr>
                                        <th className="text-left py-1 pr-4">Workflow</th>
                                        <th className="text-left py-1 pr-4">Paused At</th>
                                        <th className="text-left py-1">Call ID</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {expandedExecs.map(ex => (
                                        <tr key={ex.sk}>
                                          <td className="py-1.5 pr-4">
                                            <WorkflowBadges types={[ex.workflowType]} />
                                          </td>
                                          <td className="py-1.5 pr-4 text-gray-500 whitespace-nowrap">{fmt(ex.stoppedAt)}</td>
                                          <td className="py-1.5 font-mono text-gray-500 truncate max-w-[400px]">{ex.callId || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}

                      {/* Per-day Load More button */}
                      {dayNextKeys[day.date] && (
                        <tr>
                          <td colSpan={9} className="px-4 py-3 text-center border-t border-gray-100">
                            <button
                              onClick={() => loadMoreForDay(day.date)}
                              disabled={dayLoadingMore[day.date]}
                              className="px-4 py-1.5 text-xs bg-gray-100 text-gray-600 rounded border hover:bg-gray-200 disabled:opacity-40"
                            >
                              {dayLoadingMore[day.date] ? 'Loading…' : `Load more for ${day.date}`}
                            </button>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
